import React, { useRef, useState, useCallback } from "react"
import { useSelector } from "react-redux"
import constants from "../../Constants"
import "./CourseBuilder.css"

function apiPath(suffix) {
  const host = String(constants.HOST || "").replace(/\/+$/, "")
  const needsV1 = !host.endsWith("/v1")
  return `${host}${needsV1 ? "/v1" : ""}${suffix}`
}

// ── Format reference data ─────────────────────────────────────────────────
const SHEET_REFERENCE = [
  {
    sheet: "Course",
    columns: [
      { name: "Course Name *", note: "Unique key — same name on re-upload = UPDATE" },
      { name: "Description", note: "Optional" },
    ],
    notes: "One row only.",
  },
  {
    sheet: "Modules",
    columns: [
      { name: "Module Title *", note: "" },
      { name: "Description *", note: "" },
      { name: "Unique Identifier", note: "Leave blank to auto-generate" },
      { name: "Image URL", note: "Optional image URL for module splash screen" },
    ],
    notes: "One row per module, top → bottom = first → last module.",
  },
  {
    sheet: "Parts",
    columns: [
      { name: "Module Title *", note: "Must match Modules sheet exactly" },
      { name: "Part Title *", note: "" },
      { name: "Page Type *", note: "text-input, button-selection, video, or content-only" },
      { name: "Heading *", note: "Bold title at top of page (not shown on video-only pages)" },
      { name: "Description *", note: "Paragraph below heading (not shown on video-only pages)" },
      { name: "Input Label 1", note: "text-input: first question prompt", color: "green" },
      { name: "Input Label 2", note: "text-input: optional second prompt", color: "green" },
      { name: "Input Label 3", note: "text-input: optional third prompt", color: "green" },
      { name: "Salesforce Field 1", note: "pairs with Input Label 1; eMpowered value-only sync", color: "purple" },
      { name: "Salesforce Field 2", note: "pairs with Input Label 2 (optional)", color: "purple" },
      { name: "Salesforce Field 3", note: "pairs with Input Label 3 (optional)", color: "purple" },
      { name: "Selection Limit *", note: "button-selection required: max selections (1 … option count)", color: "blue" },
      { name: "Option 1", note: "button-selection: first choice (column M)", color: "blue" },
      { name: "Option 2 – 25", note: "button-selection: additional choices (up to 25 total)", color: "blue" },
      { name: "Video URL", note: "video: direct MP4 URL", color: "orange" },
    ],
    notes: "One row per page. For text-input fill Input Labels and optional Salesforce Fields (1:1 pairing); for button-selection fill Selection Limit (required) + Options 1–25 and optional Salesforce Field 1 only; for video fill Video URL only; for content-only fill Heading + Description only.",
  },
]

// ── Component ────────────────────────────────────────────────────────────
export default function CourseBuilder() {
  const apiToken = useSelector((state) => state.api?.apiToken || "")
  const fileInputRef = useRef(null)

  const [selectedFile, setSelectedFile] = useState(null)
  const [dragOver, setDragOver] = useState(false)
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [downloadError, setDownloadError] = useState(null)
  const [uploadStatus, setUploadStatus] = useState(null)
  const [uploading, setUploading] = useState(false)

  // ── Download template ──────────────────────────────────────────────────
  const handleDownload = useCallback(async () => {
    setDownloadLoading(true)
    setDownloadError(null)
    try {
      const res = await window.fetch(apiPath("/admin/courseTemplate"), {
        method: "GET",
        headers: { Authorization: apiToken },
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body?.message || `Server error ${res.status}`)
      }
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = "course-builder-template.xlsx"
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      setDownloadError(err?.message || "Download failed. Please try again.")
    } finally {
      setDownloadLoading(false)
    }
  }, [apiToken])

  // ── File selection ─────────────────────────────────────────────────────
  const handleFileChange = (e) => {
    const file = e.target.files?.[0]
    if (file) { setSelectedFile(file); setUploadStatus(null) }
  }

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) { setSelectedFile(file); setUploadStatus(null) }
  }, [])

  // ── Upload & upsert course ─────────────────────────────────────────────
  const handleUpload = useCallback(async () => {
    if (!selectedFile) return
    setUploading(true)
    setUploadStatus({ type: "loading", message: "Uploading and processing…" })

    try {
      const form = new FormData()
      form.append("template", selectedFile, selectedFile.name)

      const res = await window.fetch(apiPath("/admin/createCourseFromTemplate"), {
        method: "POST",
        headers: { Authorization: apiToken },
        body: form,
      })
      const body = await res.json().catch(() => ({}))

      if (res.ok || res.status === 201) {
        setUploadStatus({ type: "success", message: body.message, data: body.course, isUpdate: body.is_update })
        setSelectedFile(null)
        if (fileInputRef.current) fileInputRef.current.value = ""
      } else {
        setUploadStatus({ type: "error", message: body.message || `Server error ${res.status}` })
      }
    } catch (err) {
      setUploadStatus({ type: "error", message: err?.message || "Network error. Please try again." })
    } finally {
      setUploading(false)
    }
  }, [apiToken, selectedFile])

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="courseBuilderPage">
      <h1>Course Builder</h1>
      <p className="subtitle">
        Download the Excel template, fill in your course content, then upload it
        to create the course automatically.{" "}
        <strong>Re-uploading the same course name updates the existing course</strong>{" "}
        instead of creating a duplicate.
      </p>

      {/* ── Step 1: Download ── */}
      <div className="cbCard">
        <h2><span className="stepBadge">1</span>Download the Template</h2>
        <p>
          The template has four sheets: <strong>Instructions</strong>, <strong>Course</strong>,{" "}
          <strong>Modules</strong>, and <strong>Parts</strong>. Sample rows are included to
          guide you. Read the Instructions sheet before filling in the others.
        </p>
        <button
          className="cbDownloadBtn"
          onClick={handleDownload}
          disabled={downloadLoading}
        >
          {downloadLoading ? "⏳ Generating…" : "⬇ Download Template (.xlsx)"}
        </button>
        {downloadError && (
          <div className="cbStatus error" style={{ marginTop: 14 }}>
            <strong>Download failed</strong>{downloadError}
          </div>
        )}
      </div>

      {/* ── Step 2: Format reference ── */}
      <div className="cbCard">
        <h2><span className="stepBadge">2</span>Fill in the Template</h2>
        <p>
          Each sheet maps to a layer of the course. Required fields are marked <strong>*</strong>.
          Do not rename or reorder sheets or column headers.
        </p>

        {SHEET_REFERENCE.map((sheet) => (
          <div key={sheet.sheet} className="cbSheetBlock">
            <div className="cbSheetTitle">{sheet.sheet}</div>
            <p className="cbSheetNote">{sheet.notes}</p>
            <div className="cbColumnGrid">
              {sheet.columns.map((col) => (
                <div
                  key={col.name}
                  className={`cbColItem ${col.color === "green" ? "cbColGreen" : col.color === "blue" ? "cbColBlue" : col.color === "orange" ? "cbColOrange" : col.color === "purple" ? "cbColPurple" : ""}`}
                >
                  <strong>{col.name}</strong>
                  {col.note && <span>{col.note}</span>}
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="cbPageTypeNote">
          <div className="cbPageTypeRow">
            <span className="cbTag green">text-input</span>
            <span>
              Free-text response page. Fill <strong>Input Label 1–3</strong> and optional{" "}
              <strong>Salesforce Field 1–3</strong> (paired 1:1 for eMpowered sync to PYP_Data__c).
              Uses elements: User Input Small + Meta Data.
            </span>
          </div>
          <div className="cbPageTypeRow" style={{ marginTop: 10 }}>
            <span className="cbTag">button-selection</span>
            <span>
              Multiple-choice selection page. Fill <strong>Selection Limit</strong>{" "}
              (required — max number of options a user may select) and{" "}
              <strong>Option 1–25</strong>. Optional Salesforce Field 1 for sync.
              Uses elements: Button Selection + Meta Data.
            </span>
          </div>
          <div className="cbPageTypeRow" style={{ marginTop: 10 }}>
            <span className="cbTag orange">video</span>
            <span>
              Video playback page. Fill <strong>Video URL</strong> with a direct link
              to an MP4 file (e.g. S3 or <strong>http://localhost:PORT/...</strong>).
              Not a Digital Library video ID. Uses element: Video.
            </span>
          </div>
          <div className="cbPageTypeRow" style={{ marginTop: 10 }}>
            <span className="cbTag">content-only</span>
            <span>
              Read-only content page. Fill <strong>Heading</strong> and{" "}
              <strong>Description</strong> only — no input fields, buttons, or video.
              Uses element: Meta Data.
            </span>
          </div>
          <div className="cbPageTypeRow cbWarningRow">
            <span>⚠</span>
            <span>
              Never mix Button Selection and User Input Small on the same page — it causes a blank screen.
            </span>
          </div>
        </div>
      </div>

      {/* ── Step 3: Upload ── */}
      <div className="cbCard">
        <h2><span className="stepBadge">3</span>Upload the Completed Template</h2>
        <p>
          The system validates the data and then creates or updates the course, all
          modules, pages, question prompts, and button options automatically.
        </p>

        <div
          className={`cbUploadArea${dragOver ? " dragOver" : ""}`}
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          role="button"
          tabIndex={0}
          onKeyPress={(e) => e.key === "Enter" && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={handleFileChange}
          />
          <div className="cbUploadIcon">📂</div>
          {selectedFile ? (
            <>
              <p className="fileSelected">{selectedFile.name}</p>
              <p>{(selectedFile.size / 1024).toFixed(1)} KB — click or drag to change</p>
            </>
          ) : (
            <>
              <p>Click to select a file, or drag and drop here</p>
              <p style={{ marginTop: 4 }}>Accepted: .xlsx only</p>
            </>
          )}
        </div>

        <button
          className="cbUploadBtn"
          onClick={handleUpload}
          disabled={!selectedFile || uploading}
        >
          {uploading ? "⏳ Processing…" : "🚀 Upload & Save Course"}
        </button>

        {uploadStatus && (
          <div className={`cbStatus ${uploadStatus.type}`}>
            {uploadStatus.type === "success" && (
              <>
                <strong>
                  {uploadStatus.isUpdate ? "✅ Course Updated" : "✅ Course Created"} — {uploadStatus.message}
                </strong>
                {uploadStatus.data && (
                  <ul>
                    <li><strong>Course ID:</strong> {uploadStatus.data.id}</li>
                    <li><strong>Course Name:</strong> {uploadStatus.data.name}</li>
                    <li><strong>Course Version ID:</strong> {uploadStatus.data.course_version_id}</li>
                    {uploadStatus.data.modules_created > 0 && (
                      <li><strong>Modules created:</strong> {uploadStatus.data.modules_created}</li>
                    )}
                    {uploadStatus.data.modules_updated > 0 && (
                      <li><strong>Modules updated:</strong> {uploadStatus.data.modules_updated}</li>
                    )}
                    {uploadStatus.data.parts_created > 0 && (
                      <li><strong>Pages created:</strong> {uploadStatus.data.parts_created}</li>
                    )}
                    {uploadStatus.data.parts_updated > 0 && (
                      <li><strong>Pages updated:</strong> {uploadStatus.data.parts_updated}</li>
                    )}
                    {!uploadStatus.isUpdate && (
                      <li style={{ marginTop: 8, color: "#1b5e20" }}>
                        To assign this course to a user, insert a row into{" "}
                        <code>user_course_versions</code> with{" "}
                        <code>course_id={uploadStatus.data.id}</code> and{" "}
                        <code>course_version_id={uploadStatus.data.course_version_id}</code>.
                      </li>
                    )}
                  </ul>
                )}
              </>
            )}
            {uploadStatus.type === "error" && (
              <>
                <strong>❌ Error</strong>
                {uploadStatus.message}
              </>
            )}
            {uploadStatus.type === "loading" && (
              <strong>⏳ {uploadStatus.message}</strong>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
